import React from 'react';
import api from '../services/api';

interface Store {
  id: number;
  name: string;
  slug?: string;
  address?: string;
  city?: string;
  lat?: string | number;
  lon?: string | number;
  phone?: string;
  work_hours?: string;
  active?: boolean;
  description?: string;
}

interface Props {
  apiKey?: string; // Yandex maps API key; if not provided, map will not load
  stores: Store[];
  className?: string;
}

const loadYandexScript = (apiKey: string) => {
  return new Promise<void>((resolve, reject) => {
    if (typeof window === 'undefined') return reject(new Error('no-window'));
    // if already loaded
    if ((window as any).ymaps) return resolve();
    const existing = document.querySelector(`script[data-ymaps]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('ymaps failed to load')));
      return;
    }
    const s = document.createElement('script');
    s.setAttribute('src', `https://api-maps.yandex.ru/2.1/?lang=ru_RU&apikey=${encodeURIComponent(apiKey)}`);
    s.setAttribute('data-ymaps', '1');
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('ymaps failed to load'));
    document.head.appendChild(s);
  });
};

interface StoresMapProps extends Props {
  selectedStoreId?: number | null;
  onSelectStore?: (id: number) => void;
}

const StoresMap: React.FC<StoresMapProps> = ({ apiKey, stores, className, selectedStoreId = null, onSelectStore }) => {
  const mapRef = React.useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = React.useRef<any>(null);
  const placemarksRef = React.useRef<Map<number, any>>(new Map());

  React.useEffect(() => {
    let cancelled = false;
    const init = async () => {
      if (!apiKey) return;
      try {
        await loadYandexScript(apiKey);
        if (cancelled) return;
        const ymaps = (window as any).ymaps;
        await ymaps.ready();
        if (cancelled) return;

        // compute center: average of points or first point
        const coords = (stores || []).filter(s => s.lat && s.lon).map(s => [Number(s.lat), Number(s.lon)]);
        const center = coords.length > 0 ? coords.reduce((acc, c) => [acc[0] + c[0], acc[1] + c[1]], [0,0]).map(v => v / coords.length) : [55.76, 37.64];

        // create map if not exists
        if (!mapInstanceRef.current && mapRef.current) {
          mapInstanceRef.current = new ymaps.Map(mapRef.current, {
            center: center as [number, number],
            zoom: coords.length > 0 ? 10 : 11,
            controls: ['zoomControl', 'typeSelector', 'fullscreenControl']
          });
        }

        // clear existing geoObjects
        mapInstanceRef.current.geoObjects.removeAll();

        // add placemarks
        placemarksRef.current.clear();
        (stores || []).forEach((s: Store) => {
          if (!s.lat || !s.lon) return;
          try {
            const coord = [Number(s.lat), Number(s.lon)];
            const placemark = new ymaps.Placemark(coord, {
              balloonContentHeader: s.name,
              balloonContentBody: `\u{1F4CD} ${s.address || ''}<br>${s.phone ? `Тел: ${s.phone}` : ''}<br>${s.work_hours ? `Часы: ${s.work_hours}` : ''}`,
              hintContent: s.name
            }, {
              preset: 'islands#icon',
              iconColor: '#FF6B6B'
            });
            placemark.events.add('click', () => {
              try {
                if (onSelectStore && typeof s.id !== 'undefined') onSelectStore(s.id);
                mapInstanceRef.current.setCenter(coord);
                placemark.balloon.open();
              } catch (e) {
                // ignore
              }
            });
            mapInstanceRef.current.geoObjects.add(placemark);
            if (typeof s.id !== 'undefined') placemarksRef.current.set(Number(s.id), placemark);
          } catch (e) {
            console.warn('Invalid store coords', s);
          }
        });

        // adjust bounds if there are points
        if (coords.length > 1) {
          const bounds = mapInstanceRef.current.geoObjects.getBounds();
          if (bounds) mapInstanceRef.current.setBounds(bounds, { checkZoomRange: true, zoomMargin: 40 });
        } else if (coords.length === 1) {
          mapInstanceRef.current.setCenter(coords[0]);
          mapInstanceRef.current.setZoom(15);
        }
        // If a store is selected externally, center and open its balloon
        if (selectedStoreId && placemarksRef.current.has(Number(selectedStoreId))) {
          const pm = placemarksRef.current.get(Number(selectedStoreId));
          try {
            const pCoord = pm.geometry.getCoordinates();
            mapInstanceRef.current.setCenter(pCoord);
            pm.balloon.open();
          } catch (e) {}
        }
      } catch (err) {
        console.error('Failed to load/init ymaps', err);
      }
    };

    init();
    return () => { cancelled = true; };
  }, [apiKey, stores]);

  // react to external selectedStoreId changes
  React.useEffect(() => {
    if (!mapInstanceRef.current) return;
    if (!selectedStoreId) return;
    const pm = placemarksRef.current.get(Number(selectedStoreId));
    if (!pm) return;
    try {
      const coord = pm.geometry.getCoordinates();
      mapInstanceRef.current.setCenter(coord);
      pm.balloon.open();
    } catch (e) {
      // ignore
    }
  }, [selectedStoreId]);

  return (
    <div className={className}>
      {/* fill parent's height so page controls overall map size (use vh or explicit classes on parent) */}
      <div ref={mapRef} className="w-full md:h-[500px] rounded border border-divider" />
    </div>
  );
};

export default StoresMap;
